import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Input from '@zg-rentals/multibrand-ui/lib/Input';
import CurrencyInput from '@zg-rentals/multibrand-ui/lib/CurrencyInput';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import { isPaymentPlanNoteLengthValid, validateInputs } from '../validation';
import { PAYMENT_PLAN_NOTE_MAX_LENGTH } from '@rental-manager/constants/PaymentConstants';
import errorMessages from '@common/constants/errorMessages';
import { dollarsToCents } from '@common/paymentsUtils/currencyUtils';
import { OneTimePaymentRequestInterface } from '@rental-manager/pages/Dashboard/types';
import StringConstants from '@common/constants/StringConstants';

type Props = {
  selectedTenantIndex: number;
  rentTermsKeyArray: string[];
  tenantNameArray: string[];
  onSubmit: (args: OneTimePaymentRequestInterface) => void;
  onClose: () => void;
  handleTenantChange: (index: number) => void;
  paymentPlanFeaturePermissions: Record<string, any>;
  paymentPlanNumber: number;
};

type PaymentPlanProps = OneTimePaymentRequestInterface & {
  tenantName: string;
};

const PaymentPlanInstallmentRequestPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const defaultState = {
    paymentPlanNote: '',
    paymentPlanAmount: '',
    errors: {
      paymentPlanNote: '',
      paymentPlanAmount: '',
      tenantName: '',
    },
  };
  const [paymentPlanValue, setPaymentPlanValue] = React.useState<Record<string, any>>(defaultState);

  const getTenantNameOptions = (): React.ReactNode => {
    const { tenantNameArray } = props;
    return tenantNameArray.map((tenantName, index) => {
      return (
        <option key={index} value={index}>
          {tenantName}
        </option>
      );
    });
  };

  const getNotesHelperText = (): string => {
    const notesHelp = `${paymentPlanValue.paymentPlanNote.length}/${PAYMENT_PLAN_NOTE_MAX_LENGTH}`;
    return paymentPlanValue.errors.paymentPlanNote ? '' : notesHelp;
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value;
    let error = '';

    if (!isPaymentPlanNoteLengthValid(value)) {
      value = value.substring(0, PAYMENT_PLAN_NOTE_MAX_LENGTH);
      error = errorMessages.PAYMENT_PLAN_NOTE_MAX_LENGTH;
    }

    setPaymentPlanValue({
      ...paymentPlanValue,
      paymentPlanNote: value,
      errors: { ...paymentPlanValue.errors, paymentPlanNote: error },
    });
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPaymentPlanValue({
      ...paymentPlanValue,
      paymentPlanAmount: e.target.value,
      errors: { ...paymentPlanValue.errors, paymentPlanAmount: '' },
    });
  };

  const handleTenantChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    props.handleTenantChange(Number(e.target.value));
  };

  const onSubmit = async (): Promise<void> => {
    if (!validateInputs(props.paymentPlanFeaturePermissions, paymentPlanValue, setPaymentPlanValue)) {
      return;
    }
    const request: PaymentPlanProps = {
      rentTermsKey: props.rentTermsKeyArray[props.selectedTenantIndex],
      amount: dollarsToCents(paymentPlanValue.paymentPlanAmount),
      type: 'payment-plan',
      notes: paymentPlanValue.paymentPlanNote,
      tenantName: props.tenantNameArray[props.selectedTenantIndex],
    };
    props.onSubmit(request);
  };

  return (
    <>
      <Row>
        <Col>
          <Text type="h3">Payment plan installment {props.paymentPlanNumber}</Text>
        </Col>
        <Col>
          <Text>
            Set up a separate payment request for each payment plan installment.&nbsp;
            <Link href={StringConstants.LINKS.LEARN_ABOUT_PAYMENT_PLAN_FAQ.LANDLORD} target="_blank">
              Learn more about payment plans
            </Link>
          </Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <CurrencyInput
            label="Amount"
            placeholder="Enter amount"
            value={paymentPlanValue.paymentPlanAmount}
            error={paymentPlanValue.errors.paymentPlanAmount}
            onChange={handleAmountChange}
            onBlur={handleAmountChange}
            onFocus={handleAmountChange}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Select
            label="Who's it for?"
            value={props.selectedTenantIndex}
            error={paymentPlanValue.errors.tenantName}
            onChange={handleTenantChange}
          >
            {getTenantNameOptions()}
          </Select>
        </Col>
      </Row>
      <Row>
        <Col>
          <Input
            label="Notes"
            placeholder="Enter notes (example: Due 04/01/21)"
            value={paymentPlanValue.paymentPlanNote}
            error={paymentPlanValue.errors.paymentPlanNote}
            help={getNotesHelperText()}
            onChange={handleNotesChange}
            onBlur={handleNotesChange}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Text>
            <strong>TIP:</strong> Use the Notes field to add a due date for this installment to help your tenant stay on
            track with their payment plan schedule.
          </Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button type="primary" full onClick={onSubmit}>
            Request payment
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button type="tertiary" full onClick={props.onClose}>
            Cancel
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default PaymentPlanInstallmentRequestPage;
